import type {
  AdminChatListItem,
  ChatAdminSummary,
  ChatAttachment,
  ChatMessage,
  ChatMessageWindow,
  ChatSearchResult,
  PatientChatThread,
} from '../types/admin';
import { mockPatients } from './patients';

const now = Date.now();

export const chatAdminRoster: readonly ChatAdminSummary[] = [
  {
    admin_id: 'admin-001',
    user_id: 'user-admin-001',
    full_name: 'Dr. Andrii Kovalenko',
    initials: 'AK',
    accent_color: '#163b1e',
    is_online: true,
    avatar_url: null,
  },
  {
    admin_id: 'admin-002',
    user_id: 'user-admin-002',
    full_name: 'Olena Marchenko',
    initials: 'OM',
    accent_color: '#407374',
    is_online: true,
    avatar_url: null,
  },
  {
    admin_id: 'admin-003',
    user_id: 'user-admin-003',
    full_name: 'Iryna Hnatenko',
    initials: 'IH',
    accent_color: '#68772b',
    is_online: false,
    avatar_url: null,
  },
  {
    admin_id: 'admin-004',
    user_id: 'user-admin-004',
    full_name: 'Nazar Petryk',
    initials: 'NP',
    accent_color: '#234748',
    is_online: true,
    avatar_url: null,
  },
];

type MutableThread = {
  patient_id: string;
  patient_name: string;
  patient_display_id: string | null;
  patient_avatar_url: string | null;
  assigned_admins: ChatAdminSummary[];
  messages: ChatMessage[];
  unread_count: number;
  manually_unread?: boolean;
  last_message_at: string | null;
};

function senderAvatarUrl(
  sender: ChatAdminSummary | { user_id: string; full_name: string },
): string | null {
  return 'avatar_url' in sender ? sender.avatar_url : null;
}

function buildMessage(
  patientId: string,
  sender: ChatAdminSummary | { user_id: string; full_name: string },
  senderRole: 'admin' | 'patient',
  messageText: string,
  minutesAgo: number,
): ChatMessage {
  return {
    id: crypto.randomUUID?.() ?? `chat-msg-${patientId}-${minutesAgo}`,
    patient_id: patientId,
    sender_user_id: sender.user_id,
    sender_role: senderRole,
    sender_name: sender.full_name,
    sender_avatar_url: senderAvatarUrl(sender),
    message_text: messageText,
    message_type: 'text',
    created_at: new Date(now - minutesAgo * 60000).toISOString(),
    is_edited: false,
    attachments: [],
  };
}

function buildVoiceMessage(
  patientId: string,
  sender: ChatAdminSummary | { user_id: string; full_name: string },
  senderRole: 'admin' | 'patient',
  durationMs: number,
  minutesAgo: number,
): ChatMessage {
  const messageId = crypto.randomUUID?.() ?? `chat-voice-${patientId}-${minutesAgo}`;
  const attachment: ChatAttachment = {
    id: `${messageId}-attachment`,
    attachment_type: 'voice',
    content_url: `mock://voice/${messageId}`,
    original_filename: null,
    mime_type: 'audio/mpeg',
    byte_size: 2048,
    duration_ms: durationMs,
    waveform: [8, 24, 18, 30, 12, 26, 14, 10],
  };

  return {
    id: messageId,
    patient_id: patientId,
    sender_user_id: sender.user_id,
    sender_role: senderRole,
    sender_name: sender.full_name,
    sender_avatar_url: senderAvatarUrl(sender),
    message_text: '',
    message_type: 'voice',
    created_at: new Date(now - minutesAgo * 60000).toISOString(),
    is_edited: false,
    attachments: [attachment],
  };
}

function buildFileMessage(
  patientId: string,
  sender: ChatAdminSummary | { user_id: string; full_name: string },
  senderRole: 'admin' | 'patient',
  filename: string,
  byteSize: number,
  minutesAgo: number,
): ChatMessage {
  const messageId = crypto.randomUUID?.() ?? `chat-file-${patientId}-${minutesAgo}`;
  const attachment: ChatAttachment = {
    id: `${messageId}-attachment`,
    attachment_type: 'file',
    content_url: `mock://file/${messageId}`,
    original_filename: filename,
    mime_type: 'application/pdf',
    byte_size: byteSize,
    duration_ms: null,
    waveform: null,
  };

  return {
    id: messageId,
    patient_id: patientId,
    sender_user_id: sender.user_id,
    sender_role: senderRole,
    sender_name: sender.full_name,
    sender_avatar_url: senderAvatarUrl(sender),
    message_text: '',
    message_type: 'file',
    created_at: new Date(now - minutesAgo * 60000).toISOString(),
    is_edited: false,
    attachments: [attachment],
  };
}

function createInitialThreads(): Map<string, MutableThread> {
  const threads = new Map<string, MutableThread>();

  mockPatients.slice(0, 7).forEach((patient, index) => {
    const patientName = `${patient.first_name} ${patient.last_name}`;
    const primaryAdmin = chatAdminRoster[index % chatAdminRoster.length]!;
    const secondaryAdmin = chatAdminRoster[(index + 1) % chatAdminRoster.length]!;
    const assignedAdmins = index % 2 === 0 ? [primaryAdmin, secondaryAdmin] : [primaryAdmin];
    const messages = [
      buildMessage(
        patient.id,
        { user_id: patient.user_id ?? `user-${patient.id}`, full_name: patientName },
        'patient',
        index % 2 === 0
          ? 'Good afternoon, could you confirm my next appointment?'
          : 'I have a question about the current aligner stage.',
        180 - index * 12,
      ),
      buildMessage(
        patient.id,
        primaryAdmin,
        'admin',
        index % 2 === 0
          ? 'You are booked for 13 February at 14:30. We will send the reminder in the morning.'
          : 'I checked your case. Please continue with the current tray for two more days.',
        150 - index * 10,
      ),
      buildMessage(
        patient.id,
        { user_id: patient.user_id ?? `user-${patient.id}`, full_name: patientName },
        'patient',
        index % 3 === 0
          ? 'Perfect, thank you. I will be there on time.'
          : 'Understood. Should I upload new photos before the switch?',
        60 - index * 4,
      ),
    ];

    if (index === 1) {
      messages.push(buildVoiceMessage(patient.id, primaryAdmin, 'admin', 18000, 16));
    }

    threads.set(patient.id, {
      patient_id: patient.id,
      patient_name: patientName,
      patient_display_id: patient.display_id ?? null,
      patient_avatar_url: patient.avatar_url ?? null,
      assigned_admins: assignedAdmins,
      messages,
      unread_count: index % 3,
      last_message_at: messages[messages.length - 1]?.created_at ?? null,
    });
  });

  return threads;
}

const threadStore = createInitialThreads();

function cloneAdmin(admin: ChatAdminSummary): ChatAdminSummary {
  return { ...admin };
}

function cloneMessage(message: ChatMessage): ChatMessage {
  return {
    ...message,
    attachments: message.attachments.map((attachment) => ({ ...attachment })),
  };
}

function cloneThread(thread: MutableThread): PatientChatThread {
  return {
    patient_id: thread.patient_id,
    patient_name: thread.patient_name,
    patient_display_id: thread.patient_display_id,
    patient_avatar_url: thread.patient_avatar_url,
    assigned_admins: thread.assigned_admins.map(cloneAdmin),
    messages: thread.messages.map(cloneMessage),
    unread_count: thread.unread_count,
    manually_unread: thread.manually_unread ?? false,
    last_message_at: thread.last_message_at,
  };
}

function getThreadRecord(patientId: string): MutableThread | undefined {
  return threadStore.get(patientId);
}

export async function mockListChats(): Promise<readonly AdminChatListItem[]> {
  return Array.from(threadStore.values())
    .map((thread) => ({
      patient_id: thread.patient_id,
      patient_name: thread.patient_name,
      patient_display_id: thread.patient_display_id,
      patient_avatar_url: thread.patient_avatar_url,
      assigned_admins: thread.assigned_admins.map(cloneAdmin),
      last_message_preview: buildLastMessagePreview(thread.messages[thread.messages.length - 1]),
      last_message_at: thread.last_message_at ?? new Date(now).toISOString(),
      unread_count: thread.unread_count,
      manually_unread: thread.manually_unread ?? false,
    }))
    .sort((left, right) => right.last_message_at.localeCompare(left.last_message_at));
}

export async function mockGetChat(patientId: string): Promise<PatientChatThread | null> {
  const thread = getThreadRecord(patientId);
  return thread ? cloneThread(thread) : null;
}

const MOCK_EMPTY_PAGE = {
  has_more_before: false,
  has_more_after: false,
  oldest_cursor: null,
  newest_cursor: null,
  page_size: 30,
} as const;

export async function mockGetMessageWindow(
  patientId: string,
  _options: { before?: string; after?: string; around?: string; limit?: number },
): Promise<ChatMessageWindow> {
  const thread = getThreadRecord(patientId);
  return {
    messages: thread ? thread.messages.map(cloneMessage) : [],
    page: MOCK_EMPTY_PAGE,
  };
}

export async function mockSearchChat(patientId: string, query: string): Promise<ChatSearchResult> {
  const thread = getThreadRecord(patientId);
  const normalized = query.trim().toLowerCase();
  const hits = (thread?.messages ?? [])
    .filter(
      (message) =>
        message.message_type === 'text' && message.message_text.toLowerCase().includes(normalized),
    )
    .sort((left, right) => right.created_at.localeCompare(left.created_at))
    .map((message) => ({
      id: message.id,
      created_at: message.created_at,
      snippet: message.message_text,
      sender_role: message.sender_role,
      sender_display_name: message.sender_name,
    }));
  return { hits, total: hits.length, next_cursor: null, query };
}

export async function mockSendChatMessage(
  patientId: string,
  messageText: string,
  senderName: string,
  senderUserId: string,
): Promise<ChatMessage> {
  const thread = getThreadRecord(patientId);
  if (!thread) {
    throw new Error(`Unknown patient chat: ${patientId}`);
  }

  const nextMessage = buildMessage(
    patientId,
    { user_id: senderUserId, full_name: senderName },
    'admin',
    messageText,
    0,
  );

  thread.messages = [...thread.messages, nextMessage];
  thread.last_message_at = nextMessage.created_at;
  thread.unread_count = 0;
  return cloneMessage(nextMessage);
}

export async function mockSendVoiceMessage(
  patientId: string,
  durationMs: number,
  senderName: string,
  senderUserId: string,
): Promise<ChatMessage> {
  const thread = getThreadRecord(patientId);
  if (!thread) {
    throw new Error(`Unknown patient chat: ${patientId}`);
  }

  const nextMessage = buildVoiceMessage(
    patientId,
    { user_id: senderUserId, full_name: senderName },
    'admin',
    durationMs,
    0,
  );

  thread.messages = [...thread.messages, nextMessage];
  thread.last_message_at = nextMessage.created_at;
  thread.unread_count = 0;
  return cloneMessage(nextMessage);
}

export async function mockSendFileMessage(
  patientId: string,
  file: File,
  senderName: string,
  senderUserId: string,
  filename: string | null,
): Promise<ChatMessage> {
  const thread = getThreadRecord(patientId);
  if (!thread) {
    throw new Error(`Unknown patient chat: ${patientId}`);
  }

  const nextMessage = buildFileMessage(
    patientId,
    { user_id: senderUserId, full_name: senderName },
    'admin',
    filename ?? file.name ?? 'chat-attachment',
    file.size,
    0,
  );

  thread.messages = [...thread.messages, nextMessage];
  thread.last_message_at = nextMessage.created_at;
  thread.unread_count = 0;
  return cloneMessage(nextMessage);
}

export async function mockMarkChatRead(patientId: string): Promise<void> {
  const thread = getThreadRecord(patientId);
  if (thread) {
    thread.unread_count = 0;
    thread.manually_unread = false;
  }
}

export async function mockMarkChatUnread(patientId: string): Promise<void> {
  const thread = getThreadRecord(patientId);
  if (thread) thread.manually_unread = true;
}

export async function mockAssignChatAdmins(
  patientId: string,
  adminIds: readonly string[],
): Promise<readonly ChatAdminSummary[]> {
  const thread = getThreadRecord(patientId);
  if (!thread) {
    throw new Error(`Unknown patient chat: ${patientId}`);
  }

  const assigned = chatAdminRoster.filter((admin) => adminIds.includes(admin.admin_id)).map(cloneAdmin);
  thread.assigned_admins = assigned;
  return assigned;
}

function buildLastMessagePreview(message: ChatMessage | undefined): string {
  if (!message) {
    return 'No messages yet';
  }
  if (message.message_type === 'voice') {
    const duration = formatDuration(message.attachments[0]?.duration_ms ?? null);
    return duration ? `Voice message · ${duration}` : 'Voice message';
  }
  if (message.message_type === 'file') {
    return message.attachments[0]?.original_filename
      ? `File attachment: ${message.attachments[0].original_filename}`
      : 'File attachment';
  }
  return message.message_text || 'No messages yet';
}

function formatDuration(durationMs: number | null): string | null {
  if (!durationMs || durationMs <= 0) {
    return null;
  }
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
